import { useEffect, useMemo, useState } from "react";
import { FileDown, MessageSquare, Send, Trash2, RefreshCw, FileText, CheckCircle2, Clock, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  submissionApi,
  triggerDownload,
} from "@/lib/submissionApi";

const MONTHS = [
  "Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec",
];

function humanSize(n) {
  if (!n) return "0 KB";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

// Admin Messages tab — lists every school submission grouped by school,
// allows downloading each file, posting a short reply back to the school,
// and deleting the submission entirely.
export default function AdminMessages() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openIds, setOpenIds] = useState(new Set()); // expanded submission ids
  const [replyDrafts, setReplyDrafts] = useState({}); // id → text
  const [replying, setReplying] = useState({}); // id → bool

  const refresh = async () => {
    setLoading(true);
    try { setItems(await submissionApi.list()); }
    catch (err) { toast.error(err?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  // Group items by school (schoolId) so each school's pile is one card.
  const groups = useMemo(() => {
    const m = new Map();
    for (const s of items) {
      const key = s.schoolId || "_unknown";
      if (!m.has(key)) m.set(key, { schoolName: s.schoolName, udiseCode: s.udiseCode, list: [] });
      m.get(key).list.push(s);
    }
    return [...m.values()].sort((a, b) => (a.schoolName || "").localeCompare(b.schoolName || ""));
  }, [items]);

  const toggleOpen = (id) => {
    setOpenIds((cur) => {
      const next = new Set(cur);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleDownload = async (subId, file) => {
    try {
      const blob = await submissionApi.downloadFile(subId, file.id);
      triggerDownload(blob);
    } catch (err) {
      toast.error("Download failed");
    }
  };

  const handleDelete = async (subId) => {
    if (!window.confirm("Delete this submission and all its files? This cannot be undone.")) return;
    try {
      await submissionApi.remove(subId);
      toast.success("Deleted");
      await refresh();
    } catch (err) { toast.error("Delete failed"); }
  };

  const handleReply = async (subId) => {
    const text = (replyDrafts[subId] || "").trim();
    if (!text) { toast.error("Type a reply first"); return; }
    setReplying((s) => ({ ...s, [subId]: true }));
    try {
      await submissionApi.reply(subId, text);
      toast.success("Reply sent to school");
      setReplyDrafts((d) => ({ ...d, [subId]: "" }));
      await refresh();
    } catch (err) { toast.error("Reply failed"); }
    finally { setReplying((s) => ({ ...s, [subId]: false })); }
  };

  return (
    <section className="space-y-4" data-testid="admin-messages-tab">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-semibold text-ink-900">Submission Messages</h2>
          <p className="text-xs text-ink-700">{items.length} submission(s) · {groups.length} school(s)</p>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} data-testid="admin-messages-refresh-btn">
          <RefreshCw className="mr-1 h-3.5 w-3.5" /> Refresh
        </Button>
      </header>

      {loading ? (
        <p className="rounded-xl bg-sand-50 px-3 py-2 text-xs text-ink-700">Loading…</p>
      ) : groups.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-border bg-white/50 p-8 text-center">
          <MessageSquare className="mx-auto h-8 w-8 text-ink-500" />
          <p className="mt-2 text-sm text-ink-700">No submissions yet. Schools will appear here as they upload register photos.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <div key={g.udiseCode} className="rounded-2xl border border-border bg-white shadow-[0_1px_0_rgba(44,53,40,0.04)]" data-testid={`admin-messages-school-${g.udiseCode}`}>
              <div className="flex items-center justify-between border-b border-border px-5 py-3">
                <div>
                  <p className="font-heading text-base font-bold text-ink-900">{g.schoolName || "Unknown school"}</p>
                  <p className="text-xs text-ink-700">UDISE: <b className="text-ink-900">{g.udiseCode || "—"}</b> · {g.list.length} submission(s)</p>
                </div>
              </div>

              <ul className="divide-y divide-border">
                {g.list.map((s) => {
                  const open = openIds.has(s.id);
                  return (
                    <li key={s.id} className="px-5 py-3" data-testid={`admin-submission-${s.id}`}>
                      <button
                        type="button"
                        onClick={() => toggleOpen(s.id)}
                        className="flex w-full items-center justify-between gap-3 text-left"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-ink-900">
                            {MONTHS[(s.month || 1) - 1]} {s.year}
                            <span className="ml-2 text-[0.7rem] font-normal text-ink-700">
                              · {(s.files || []).length} file(s)
                            </span>
                          </p>
                          <p className="text-[0.7rem] text-ink-700">{new Date(s.createdAt).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" })}</p>
                        </div>
                        {s.status === "replied" ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-moss/15 px-2.5 py-0.5 text-[0.65rem] font-semibold text-moss-dark">
                            <CheckCircle2 className="h-3 w-3" /> Replied
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-ochre/15 px-2.5 py-0.5 text-[0.65rem] font-semibold text-ochre">
                            <Clock className="h-3 w-3" /> Pending
                          </span>
                        )}
                        {open ? <ChevronUp className="h-4 w-4 text-clay" /> : <ChevronDown className="h-4 w-4 text-clay" />}
                      </button>

                      {open && (
                        <div className="mt-3 space-y-3" data-testid={`admin-submission-${s.id}-body`}>
                          {s.note && <p className="rounded-lg bg-sand-50 px-3 py-2 text-xs italic text-ink-700">"{s.note}"</p>}

                          <ul className="space-y-1.5">
                            {(s.files || []).map((f) => (
                              <li key={f.id} className="flex items-center gap-2 rounded-lg border border-border bg-white px-3 py-1.5">
                                <FileText className="h-3.5 w-3.5 shrink-0 text-clay" />
                                <span className="min-w-0 flex-1 truncate text-xs text-ink-900">{f.name}</span>
                                <span className="shrink-0 text-[0.65rem] text-ink-700">{humanSize(f.size)}</span>
                                <button
                                  type="button"
                                  onClick={() => handleDownload(s.id, f)}
                                  data-testid={`admin-submission-${s.id}-download-${f.id}`}
                                  className="grid h-7 w-7 place-items-center rounded-full text-clay hover:bg-clay/10"
                                  aria-label="Download"
                                >
                                  <FileDown className="h-3.5 w-3.5" />
                                </button>
                              </li>
                            ))}
                          </ul>

                          {/* Reply box */}
                          <div className="rounded-xl border border-moss/30 bg-moss/5 p-3">
                            <p className="flex items-center gap-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-moss-dark">
                              <MessageSquare className="h-3 w-3" /> Reply to school
                            </p>
                            {s.adminReply && (
                              <p className="mt-2 whitespace-pre-line rounded-lg bg-white px-3 py-2 text-xs text-ink-900" data-testid={`admin-submission-${s.id}-existing-reply`}>
                                {s.adminReply}
                              </p>
                            )}
                            <textarea
                              rows={2}
                              value={replyDrafts[s.id] ?? s.adminReply ?? ""}
                              onChange={(e) => setReplyDrafts((d) => ({ ...d, [s.id]: e.target.value }))}
                              placeholder="Hi, your bill is ready on WhatsApp…"
                              data-testid={`admin-submission-${s.id}-reply-textarea`}
                              className="mt-2 block w-full resize-y rounded-lg border border-border bg-white px-3 py-2 text-xs text-ink-900 outline-none focus:border-moss focus:ring-1 focus:ring-moss/30"
                            />
                            <div className="mt-2 flex items-center justify-end gap-2">
                              <Button
                                onClick={() => handleDelete(s.id)}
                                size="sm"
                                variant="ghost"
                                data-testid={`admin-submission-${s.id}-delete-btn`}
                                className="gap-1.5 text-destructive hover:bg-red-50 hover:text-destructive"
                              >
                                <Trash2 className="h-3.5 w-3.5" /> Delete
                              </Button>
                              <Button
                                onClick={() => handleReply(s.id)}
                                disabled={!!replying[s.id]}
                                size="sm"
                                data-testid={`admin-submission-${s.id}-reply-btn`}
                                className="gap-1.5 bg-moss text-white hover:bg-moss-dark"
                              >
                                <Send className="h-3.5 w-3.5" /> {replying[s.id] ? "Sending…" : "Send reply"}
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
